import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { eq } from 'tio-ui/utilities';
import Chart from 'tio-reimagined/components/chart';
import type { ChartDataPoint } from 'tio-common/types/chart';
import Card from 'tio-reimagined/components/card';
import StatCard from 'tio-reimagined/components/stat-card';
import {
  PhArrowLeft,
  PhDownload,
  PhFunnel,
  PhCalendar,
  PhTrendUp,
  PhTrendDown,
  PhUsers,
  PhWarning,
  PhCheckCircle,
  PhArrowRight,
  PhChartBar,
  PhChartLine,
  PhChartPie,
} from 'ember-phosphor-icons';

// Mock pipeline report data
const REPORT_DATA = {
  summary: {
    totalInPipeline: 85,
    readyNowRatio: 0.14,
    avgTimeToReady: 18, // months
    riskyCoverage: 3, // positions with < 1 ready candidate
    projectedNeeds: 22, // next 24 months
    pipelineHealth: 'moderate',
  },
  pipelineFlow: {
    identified: 120,
    inProgram: 85,
    developing: 28,
    readyNow: 12,
    promoted: 8,
    attrition: 15,
  },
  stageDistribution: [
    { category: 'Emerging Leaders', value: 45 },
    { category: 'Developing', value: 28 },
    { category: 'Ready Now', value: 12 },
  ],
  monthlyProgress: [
    { category: 'Jul 2025', value: 68 },
    { category: 'Aug 2025', value: 72 },
    { category: 'Sep 2025', value: 75 },
    { category: 'Oct 2025', value: 78 },
    { category: 'Nov 2025', value: 82 },
    { category: 'Dec 2025', value: 85 },
  ],
  competencyScores: [
    { category: 'Strategic Thinking', value: 72 },
    { category: 'Team Leadership', value: 78 },
    { category: 'Change Management', value: 65 },
    { category: 'Financial Acumen', value: 58 },
    { category: 'Communication', value: 82 },
    { category: 'Decision Making', value: 70 },
  ],
  departmentPipeline: [
    { category: 'Nursing', value: 32 },
    { category: 'Clinical Services', value: 18 },
    { category: 'Operations', value: 15 },
    { category: 'Administration', value: 12 },
    { category: 'IT', value: 8 },
  ],
  roleRisk: [
    { role: 'Nurse Manager', coverage: 2.5, status: 'healthy', successors: 5, openings: 2 },
    { role: 'Clinical Director', coverage: 1.0, status: 'at-risk', successors: 2, openings: 2 },
    { role: 'Department Head', coverage: 0.5, status: 'critical', successors: 1, openings: 2 },
    { role: 'VP Operations', coverage: 1.5, status: 'moderate', successors: 3, openings: 2 },
    { role: 'Chief Nursing Officer', coverage: 2.0, status: 'healthy', successors: 2, openings: 1 },
  ],
  programROI: [
    { category: 'Leadership Foundations', value: 245 },
    { category: 'Executive Development', value: 312 },
    { category: 'New Manager Bootcamp', value: 178 },
    { category: 'Healthcare Management', value: 156 },
  ],
  timeInStage: [
    { category: 'Emerging (target: 12mo)', value: 8 },
    { category: 'Developing (target: 18mo)', value: 14 },
    { category: 'Ready Now (target: 6mo)', value: 4 },
  ],
  promotionTrend: [
    { category: 'Q1 2025', value: 3 },
    { category: 'Q2 2025', value: 5 },
    { category: 'Q3 2025', value: 4 },
    { category: 'Q4 2025', value: 8 },
  ],
  diversityMetrics: [
    { category: 'Women', value: 58 },
    { category: 'Men', value: 42 },
  ],
  diversityByStage: [
    { category: 'Emerging - Women', value: 62 },
    { category: 'Emerging - Men', value: 38 },
    { category: 'Developing - Women', value: 54 },
    { category: 'Developing - Men', value: 46 },
    { category: 'Ready Now - Women', value: 50 },
    { category: 'Ready Now - Men', value: 50 },
  ],
};

export default class CareerPathwaysAdminPipelineReport extends Component {
  @tracked selectedTimeframe = '12months';
  @tracked selectedDepartment = 'all';

  get data() {
    return REPORT_DATA;
  }

  get stageDistribution(): ChartDataPoint[] {
    return this.data.stageDistribution;
  }

  get monthlyProgress(): ChartDataPoint[] {
    return this.data.monthlyProgress;
  }

  get competencyScores(): ChartDataPoint[] {
    return this.data.competencyScores;
  }

  get departmentPipeline(): ChartDataPoint[] {
    return this.data.departmentPipeline;
  }

  get programROI(): ChartDataPoint[] {
    return this.data.programROI;
  }

  get timeInStage(): ChartDataPoint[] {
    return this.data.timeInStage;
  }

  get promotionTrend(): ChartDataPoint[] {
    return this.data.promotionTrend;
  }

  get diversityMetrics(): ChartDataPoint[] {
    return this.data.diversityMetrics;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0 pb-12 pipeline-report-charts">
      {{! Header with actions }}
      <div class="flex justify-between items-center mb-6">
        <div class="tio-row-loose">
          <a href="/reimagined/admin/leadership" class="tio-fg-muted hover:tio-fg-secondary">
            <PhArrowLeft @size={{20}} />
          </a>
          <div>
            <Heading @level={{1}} class="app-page-title">
              Leadership Pipeline Report
            </Heading>
            <Text @variant="muted">Comprehensive analysis of succession planning health</Text>
          </div>
        </div>
        <div class="flex gap-3">
          <Button @appearance="outlined">
            <PhCalendar @size={{18}} class="mr-2" />
            Last 12 Months
          </Button>
          <Button @appearance="outlined">
            <PhFunnel @size={{18}} class="mr-2" />
            All Departments
          </Button>
          <Button @intent="primary">
            <PhDownload @size={{18}} class="mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {{! Executive Summary Cards }}
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <StatCard
          @compact={{true}}
          @label="In Pipeline"
          @value={{this.data.summary.totalInPipeline}}
          @icon={{PhUsers}}
          @iconColor="violet"
          @trend="up"
          @trendText="+12% YoY"
        />

        <StatCard
          @compact={{true}}
          @label="Ready Now"
          @value={{this.data.pipelineFlow.readyNow}}
          @icon={{PhCheckCircle}}
          @iconColor="green"
          @secondaryText="14% of pipeline"
        />

        <StatCard
          @compact={{true}}
          @label="Promoted YTD"
          @value={{this.data.pipelineFlow.promoted}}
          @icon={{PhArrowRight}}
          @iconColor="teal"
          @trend="up"
          @trendText="+33% vs prior"
        />

        <StatCard
          @compact={{true}}
          @label="Avg Time to Ready"
          @value={{this.data.summary.avgTimeToReady}}
          @suffix="mo"
          @icon={{PhChartLine}}
          @iconColor="blue"
          @secondaryText="Target: 24mo"
        />

        <StatCard
          @compact={{true}}
          @label="At-Risk Roles"
          @value={{this.data.summary.riskyCoverage}}
          @valueColor="amber"
          @icon={{PhWarning}}
          @iconColor="amber"
          @secondaryText="Need attention"
        />

        <StatCard
          @compact={{true}}
          @label="Attrition"
          @value={{this.data.pipelineFlow.attrition}}
          @icon={{PhTrendDown}}
          @iconColor="red"
          @secondaryText="18% of pipeline"
        />
      </div>

      {{! Pipeline Funnel Visualization }}
      <Card @padding="none" class="mb-6">
        <div class="tio-row-tight p-5 border-b tio-border-subtle">
          <PhFunnel @size={{20}} class="tio-fg-brand" />
          <span class="font-medium tio-fg-primary">Pipeline Flow</span>
        </div>
        <div class="p-5">
          <div class="flex items-center justify-center py-4 gap-3 flex-wrap">
            <div class="text-center">
              <div class="px-8 py-4 tio-bg-brand-wash rounded-lg border-2 tio-border-brand-soft">
                <div
                  class="tio-fg-brand text-3xl font-bold"
                >{{this.data.pipelineFlow.identified}}</div>
                <Text class="tio-fg-brand">Identified</Text>
              </div>
            </div>
            <PhArrowRight @size={{24}} class="tio-fg-disabled" />
            <div class="text-center">
              <div class="px-8 py-4 tio-bg-brand-soft rounded-lg border-2 tio-border-brand">
                <div
                  class="tio-fg-brand text-3xl font-bold"
                >{{this.data.pipelineFlow.inProgram}}</div>
                <Text class="tio-fg-brand">In Program</Text>
              </div>
            </div>
            <PhArrowRight @size={{24}} class="tio-fg-disabled" />
            <div class="text-center">
              <div
                class="px-8 py-4 bg-teal-100 dark:bg-teal-900/50 rounded-lg border-2 border-teal-200 dark:border-teal-700"
              >
                <div
                  class="text-3xl font-bold text-teal-700 dark:text-teal-300"
                >{{this.data.pipelineFlow.developing}}</div>
                <Text class="text-teal-600 dark:text-teal-400">Developing</Text>
              </div>
            </div>
            <PhArrowRight @size={{24}} class="tio-fg-disabled" />
            <div class="text-center">
              <div
                class="px-8 py-4 tio-bg-success-soft dark:bg-green-900/50 rounded-lg border-2 tio-border-success dark:border-green-700"
              >
                <div
                  class="text-3xl font-bold tio-fg-success-body dark:text-green-300"
                >{{this.data.pipelineFlow.readyNow}}</div>
                <Text class="tio-fg-success dark:text-green-400">Ready Now</Text>
              </div>
            </div>
            <PhArrowRight @size={{24}} class="tio-fg-disabled" />
            <div class="text-center">
              <div
                class="px-8 py-4 bg-green-200 dark:bg-green-800/50 rounded-lg border-2 border-green-300 dark:border-green-600"
              >
                <div
                  class="text-3xl font-bold tio-fg-success-h dark:text-green-200"
                >{{this.data.pipelineFlow.promoted}}</div>
                <Text class="tio-fg-success-body dark:text-green-400">Promoted</Text>
              </div>
            </div>
          </div>
          <div class="flex justify-center mt-4 p-3 tio-bg-warning-soft rounded-lg">
            <div class="tio-row-tight">
              <PhWarning @size={{18}} class="tio-fg-warning" />
              <Text class="tio-fg-warning-body">
                <strong>{{this.data.pipelineFlow.attrition}}</strong>
                candidates left pipeline (attrition rate: 18%)
              </Text>
            </div>
          </div>
        </div>
      </Card>

      {{! Charts Grid - Row 1 }}
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {{! Pipeline Growth Over Time }}
        <Card @padding="none">
          <div class="tio-row-tight p-5 border-b tio-border-subtle">
            <PhChartLine @size={{20}} class="text-teal-600" />
            <span class="font-medium tio-fg-primary">Pipeline Growth Trend</span>
          </div>
          <div class="p-5">
            {{#if this.monthlyProgress.length}}
              <Chart
                @data={{this.monthlyProgress}}
                @type="area"
                @xLabel="Month"
                @yLabel="Candidates"
                @height={{256}}
              />
            {{else}}
              <div class="h-64 flex items-center justify-center tio-fg-muted">
                No data available
              </div>
            {{/if}}
            <div class="mt-4 p-3 tio-bg-success-soft dark:bg-green-900/30 rounded-lg">
              <Text @size="sm" class="tio-fg-success-h dark:text-green-200">
                Pipeline has grown
                <strong>25%</strong>
                over the past 6 months
              </Text>
            </div>
          </div>
        </Card>

        {{! Stage Distribution }}
        <Card @padding="none">
          <div class="tio-row-tight p-5 border-b tio-border-subtle">
            <PhChartPie @size={{20}} class="tio-fg-brand" />
            <span class="font-medium tio-fg-primary">Stage Distribution</span>
          </div>
          <div class="p-5">
            {{#if this.stageDistribution.length}}
              <Chart @data={{this.stageDistribution}} @type="donut" />
            {{else}}
              <div class="h-64 flex items-center justify-center tio-fg-muted">
                No data available
              </div>
            {{/if}}
            <div class="flex justify-center gap-6 mt-4">
              <div class="tio-row-tight">
                <div class="w-3 h-3 rounded-full tio-bg-brand"></div>
                <Text @size="sm" class="dark:text-gray-200">Emerging (53%)</Text>
              </div>
              <div class="tio-row-tight">
                <div class="w-3 h-3 rounded-full bg-teal-500"></div>
                <Text @size="sm" class="dark:text-gray-200">Developing (33%)</Text>
              </div>
              <div class="tio-row-tight">
                <div class="w-3 h-3 rounded-full tio-bg-success"></div>
                <Text @size="sm" class="dark:text-gray-200">Ready Now (14%)</Text>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {{! Charts Grid - Row 2 }}
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {{! Competency Gaps }}
        <Card @padding="none">
          <div class="tio-row-tight p-5 border-b tio-border-subtle">
            <PhChartBar @size={{20}} class="tio-fg-info" />
            <span class="font-medium tio-fg-primary">Leadership Competency Scores</span>
          </div>
          <div class="p-5">
            {{#if this.competencyScores.length}}
              <Chart
                @data={{this.competencyScores}}
                @type="horizontalBar"
                @xLabel="Score %"
                @yLabel="Competency"
              />
            {{else}}
              <div class="h-64 flex items-center justify-center tio-fg-muted">
                No data available
              </div>
            {{/if}}
            <div class="mt-4 p-3 tio-bg-warning-soft rounded-lg">
              <Text @size="sm" class="tio-fg-warning-body">
                <strong>Financial Acumen</strong>
                and
                <strong>Change Management</strong>
                are largest gaps (target: 80%)
              </Text>
            </div>
          </div>
        </Card>

        {{! Department Pipeline }}
        <Card @padding="none">
          <div class="tio-row-tight p-5 border-b tio-border-subtle">
            <PhChartBar @size={{20}} class="tio-fg-brand" />
            <span class="font-medium tio-fg-primary">Pipeline by Department</span>
          </div>
          <div class="p-5">
            {{#if this.departmentPipeline.length}}
              <Chart
                @data={{this.departmentPipeline}}
                @type="bar"
                @xLabel="Department"
                @yLabel="Candidates"
                @height={{256}}
              />
            {{else}}
              <div class="h-64 flex items-center justify-center tio-fg-muted">
                No data available
              </div>
            {{/if}}
            <div class="mt-4 p-3 tio-bg-brand-wash rounded-lg">
              <Text @size="sm" class="tio-fg-brand">
                <strong>Nursing</strong>
                has strongest pipeline (38% of total)
              </Text>
            </div>
          </div>
        </Card>
      </div>

      {{! Charts Grid - Row 3 }}
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {{! Program ROI }}
        <Card @padding="none">
          <div class="tio-row-tight p-5 border-b tio-border-subtle">
            <PhChartBar @size={{20}} class="tio-fg-success" />
            <span class="font-medium tio-fg-primary">Program ROI %</span>
          </div>
          <div class="p-5">
            {{#if this.programROI.length}}
              <Chart
                @data={{this.programROI}}
                @type="horizontalBar"
                @xLabel="ROI %"
                @yLabel="Program"
              />
            {{else}}
              <div class="h-48 flex items-center justify-center tio-fg-muted">
                No data available
              </div>
            {{/if}}
          </div>
        </Card>

        {{! Promotions Trend }}
        <Card @padding="none">
          <div class="tio-row-tight p-5 border-b tio-border-subtle">
            <PhTrendUp @size={{20}} class="text-teal-600" />
            <span class="font-medium tio-fg-primary">Quarterly Promotions</span>
          </div>
          <div class="p-5">
            {{#if this.promotionTrend.length}}
              <Chart
                @data={{this.promotionTrend}}
                @type="line"
                @xLabel="Quarter"
                @yLabel="Promotions"
                @height={{192}}
              />
            {{else}}
              <div class="h-48 flex items-center justify-center tio-fg-muted">
                No data available
              </div>
            {{/if}}
          </div>
        </Card>

        {{! Diversity }}
        <Card @padding="none">
          <div class="tio-row-tight p-5 border-b tio-border-subtle">
            <PhUsers @size={{20}} class="tio-fg-brand" />
            <span class="font-medium tio-fg-primary">Gender Diversity</span>
          </div>
          <div class="p-5">
            {{#if this.diversityMetrics.length}}
              <Chart @data={{this.diversityMetrics}} @type="pie" />
            {{else}}
              <div class="h-48 flex items-center justify-center tio-fg-muted">
                No data available
              </div>
            {{/if}}
          </div>
        </Card>
      </div>

      {{! Role Coverage Risk Table }}
      <Card @padding="none" class="mb-6">
        <div class="tio-row-tight p-5 border-b tio-border-subtle">
          <PhWarning @size={{20}} class="tio-fg-warning" />
          <span class="font-medium tio-fg-primary">Succession Coverage by Role</span>
        </div>
        <div class="p-5">
          <table class="w-full">
            <thead>
              <tr class="border-b tio-border-default">
                <th class="text-left py-3 px-4 font-semibold tio-fg-secondary">Role</th>
                <th class="text-center py-3 px-4 font-semibold tio-fg-secondary">Ready Successors</th>
                <th class="text-center py-3 px-4 font-semibold tio-fg-secondary">Projected Openings</th>
                <th class="text-center py-3 px-4 font-semibold tio-fg-secondary">Coverage Ratio</th>
                <th class="text-center py-3 px-4 font-semibold tio-fg-secondary">Status</th>
              </tr>
            </thead>
            <tbody>
              {{#each this.data.roleRisk as |role|}}
                <tr
                  class="border-b tio-border-subtle hover:tio-bg-sunken dark:hover:bg-gray-700/50"
                >
                  <td class="py-3 px-4">
                    <Text class="font-medium tio-fg-primary">{{role.role}}</Text>
                  </td>
                  <td class="py-3 px-4 text-center">
                    <Text class="dark:text-gray-200">{{role.successors}}</Text>
                  </td>
                  <td class="py-3 px-4 text-center">
                    <Text class="dark:text-gray-200">{{role.openings}}</Text>
                  </td>
                  <td class="py-3 px-4 text-center">
                    <Text class="font-semibold">{{role.coverage}}x</Text>
                  </td>
                  <td class="py-3 px-4 text-center">
                    {{#if (eq role.status "healthy")}}
                      <Chip @intent="success">Healthy</Chip>
                    {{else if (eq role.status "moderate")}}
                      <Chip @intent="warning">Moderate</Chip>
                    {{else if (eq role.status "at-risk")}}
                      <Chip @intent="warning">At Risk</Chip>
                    {{else}}
                      <Chip @intent="danger">Critical</Chip>
                    {{/if}}
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </div>
      </Card>

      {{! Key Insights & Recommendations }}
      <Card @padding="none">
        <div class="tio-row-tight p-5 border-b tio-border-subtle">
          <PhCheckCircle @size={{20}} class="text-teal-600" />
          <span class="font-medium tio-fg-primary">Key Insights & Recommendations</span>
        </div>
        <div class="p-5">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-4">
              <Heading @level={{3}} class="text-lg font-semibold tio-fg-primary">Strengths</Heading>
              <div class="space-y-3">
                <div class="tio-row-top p-3 tio-bg-success-soft dark:bg-green-900/30 rounded-lg">
                  <PhCheckCircle @size={{20}} class="tio-fg-success dark:text-green-400 mt-0.5" />
                  <div>
                    <Text class="font-medium tio-fg-success-h dark:text-green-200">Strong pipeline
                      growth</Text>
                    <Text @size="sm" class="tio-fg-success-h dark:text-green-300">25% increase in 6
                      months exceeds 15% target</Text>
                  </div>
                </div>
                <div class="tio-row-top p-3 tio-bg-success-soft dark:bg-green-900/30 rounded-lg">
                  <PhCheckCircle @size={{20}} class="tio-fg-success dark:text-green-400 mt-0.5" />
                  <div>
                    <Text class="font-medium tio-fg-success-h dark:text-green-200">Accelerated
                      development</Text>
                    <Text @size="sm" class="tio-fg-success-h dark:text-green-300">Avg time to ready
                      (18mo) beats 24mo target by 25%</Text>
                  </div>
                </div>
                <div class="tio-row-top p-3 tio-bg-success-soft dark:bg-green-900/30 rounded-lg">
                  <PhCheckCircle @size={{20}} class="tio-fg-success dark:text-green-400 mt-0.5" />
                  <div>
                    <Text class="font-medium tio-fg-success-h dark:text-green-200">High program ROI</Text>
                    <Text @size="sm" class="tio-fg-success-h dark:text-green-300">Executive
                      Development program shows 312% ROI</Text>
                  </div>
                </div>
              </div>
            </div>
            <div class="space-y-4">
              <Heading @level={{3}} class="text-lg font-semibold tio-fg-primary">Areas for Action</Heading>
              <div class="space-y-3">
                <div class="tio-row-top p-3 tio-bg-warning-soft rounded-lg">
                  <PhWarning @size={{20}} class="tio-fg-warning mt-0.5" />
                  <div>
                    <Text class="tio-fg-warning-body font-medium">Department Head coverage critical</Text>
                    <Text @size="sm" class="tio-fg-warning-body">Only 0.5x coverage—prioritize
                      identifying candidates</Text>
                  </div>
                </div>
                <div class="tio-row-top p-3 tio-bg-warning-soft rounded-lg">
                  <PhWarning @size={{20}} class="tio-fg-warning mt-0.5" />
                  <div>
                    <Text class="tio-fg-warning-body font-medium">Financial acumen gap</Text>
                    <Text @size="sm" class="tio-fg-warning-body">58% avg score (target 80%)—add
                      finance training</Text>
                  </div>
                </div>
                <div class="tio-row-top p-3 tio-bg-warning-soft rounded-lg">
                  <PhWarning @size={{20}} class="tio-fg-warning mt-0.5" />
                  <div>
                    <Text class="tio-fg-warning-body font-medium">IT department underrepresented</Text>
                    <Text @size="sm" class="tio-fg-warning-body">Only 8 in pipeline—increase
                      identification efforts</Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </VStack>
  </template>
}
